import { useCallback, useMemo, useState } from "react";
import { FormikHelpers } from "formik";
import { toast } from "@jsq/ds/core";
import {
  useActiveArenaAccessesQuery,
  useRemoveGroupMembershipMutation,
} from "~/graphql/supergraphHooks";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import { UserRow } from "..";
import { useRemoveWorkspaceAccessModal } from "../../UserDetails/components/RemoveWorkspaceAccessModal/hooks";

export type FormValues = { roles?: string[] };

export type UserData = {
  fullName: string;
  emailAddress: string;
};

export const useBulkRemoveRolesModal = ({ users }: { users: UserRow[] }) => {
  const userIds = useMemo(() => users.map((u) => toGlobalId("UserObjectType", u.id)), [users]);
  const [usersWithoutRoles, setUsersWithoutRoles] = useState<UserData[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showUsersWithoutRolesModal, setShowUsersWithoutRolesModal] = useState(false);
  const [removeGroupMembership, { loading: mutationLoading }] =
    useRemoveGroupMembershipMutation();

  const onHide = useCallback(() => {
    setShowModal(false);
  }, []);

  const onHideUsersWithoutRolesModal = useCallback(() => {
    setShowUsersWithoutRolesModal(false);
  }, []);

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const removeCanopyRole = useCallback(
    async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
      try {
        const response = await removeGroupMembership({
          variables: {
            input: {
              arenaId: toGlobalId("ArenaObjectType", 0),
              groupIds: values?.roles ?? [],
              userIds,
            },
          },
          refetchQueries: ["GetCcToolsUser"],
        });
        const data = response.data?.removeGroupMembership;

        if (data?.__typename === "RemoveGroupMembershipResponse") {
          toast.success("Successfully updated Canopy Roles");
          resetForm();

          const orphanedUsers = data?.orphanedUsers ?? [];
          const usersNoRoles = users
            .filter((u) => orphanedUsers.includes(toGlobalId("UserObjectType", u.id)))
            .map((u) => ({
              fullName: u.name,
              emailAddress: u.email,
            }));

          if (usersNoRoles.length > 0) {
            setUsersWithoutRoles(usersNoRoles);
            setShowUsersWithoutRolesModal(true);
          }
          onHide();
        } else if (data?.__typename === "MutationErrorResponse") {
          toast.error(data.message);
        } else {
          toast.error("There was an error with the operation");
        }
      } catch {
        toast.error("There was an error with the operation");
      }
    },
    [removeGroupMembership, userIds, onHide, users],
  );

  const initialValues: FormValues = { roles: [] };

  return {
    initialValues,
    onHide,
    openModal,
    showModal,
    removeCanopyRole,
    isSubmitting: mutationLoading,
    usersWithoutRoles,
    showUsersWithoutRolesModal,
    onHideUsersWithoutRolesModal,
  };
};

export const useRemoveRoleModal = ({
  userIds,
  groupIds,
}: {
  userIds: string[];
  groupIds: string[];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [removeMembership, { loading: isSubmitting }] = useRemoveGroupMembershipMutation();
  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);
  const onHide = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleRemove = useCallback(async () => {
    try {
      const response = await removeMembership({
        variables: {
          input: {
            arenaId: toGlobalId("ArenaObjectType", 0),
            userIds,
            groupIds,
          },
        },
        refetchQueries: ["GetCcToolsUser"],
      });
      const data = response.data?.removeGroupMembership;

      if (data?.__typename === "RemoveGroupMembershipResponse") {
        onHide();
        toast.success("Successfully removed Canopy role");
      } else if (data?.__typename === "MutationErrorResponse") {
        toast.error(data.message);
      }
    } catch {
      toast.error("There was an error with the operation");
    }
  }, [onHide, removeMembership, groupIds, userIds]);
  return { isOpen, openModal, onHide, handleRemove, isSubmitting };
};

export type RemoveWorkspaceAccessModalParams = {
  userIds: number[];
  onClose: () => void;
};

export const useRemoveWorkspaceAccess = ({
  onClose,
  userIds,
}: RemoveWorkspaceAccessModalParams) => {
  const { removeWorkspaceAccess, loading: isLoading } = useRemoveWorkspaceAccessModal({
    onClose,
  });

  const globalUserIds = useMemo(() => {
    return userIds.map((id) => toGlobalId("UserObjectType", id));
  }, [userIds]);

  const fetchWorkspaceAccessResult = useActiveArenaAccessesQuery({
    variables: {
      filters: {
        userIds: globalUserIds,
      },
    },
    fetchPolicy: "network-only",
    skip: userIds.length === 0,
  });

  const workspaceAccesses = useMemo(() => {
    if (
      fetchWorkspaceAccessResult.data?.activeArenaAccesses?.__typename ===
      "ActiveArenaAccessOutput"
    ) {
      return fetchWorkspaceAccessResult.data.activeArenaAccesses.activeArenas.map((a) => ({
        id: toDbId(a.id),
        name: a.name,
      }));
    }
  }, [fetchWorkspaceAccessResult.data]);

  const handleConfirm = async ({ arenaIds }: { arenaIds?: string[] }) => {
    await removeWorkspaceAccess({
      arenaIds: arenaIds ?? [],
      userIds: userIds?.map((v) => v?.toString()),
    });
  };

  const initialValues = { arenaIds: [] };

  return {
    handleConfirm,
    isLoading,
    workspaceAccesses: workspaceAccesses || [],
    fetchWorkspaceAccessResult,
    initialValues,
  };
};
